import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import LocalStorage from '@/utils/LocalStorage';
import { validateSession } from '@/utils/Auth';
import { redirectToOnboarding, redirectToLogin } from '@/utils/Router';
import getQueryParams from '@/utils/getQueryParams';
import WalletView from '@/views/wallet/WalletView.vue';

Vue.use(VueRouter);

const isDevEnvironment = window.location.host === 'dev.emoney.exporo.com';
const emoneyDisabled = process.env.VUE_APP_EMONEY_DISABLED === 'true';

const routes = [
{
    // main init view
    path: '/wallet',
    name: 'Exporo Wallet',
    meta: { requiresAuth: true },
    component: WalletView,
    redirect: '/wallet/transactions',
    children: [
    {
        // ChargeWalletView will be rendered inside WalletView's <router-view>
        // when '/wallet/deposit' is matched
        path: 'charge',
        name: 'Charge',
        ...(emoneyDisabled
          ? { redirect: '/' }
          : { component: () => import('@/views/wallet/ChargeWalletView.vue') }),
      },
      {
        // WithdrawWalletView will be rendered inside WalletView's <router-view>
        // when '/wallet/deposit' is matched
        path: 'withdraw',
        name: 'Withdraw',
        ...(emoneyDisabled
          ? { redirect: '/' }
          : { component: () => import('@/views/wallet/WithdrawWalletView.vue') }),
      },
      {
        // TransactionsView will be rendered inside WalletView's <router-view>
        // when '/wallet/transactions' is matched
        path: 'transactions',
        name: 'Transactions',
        component: () => import('@/views/wallet/TransactionsView.vue'),
      },
    ],
  },
  {
    path: '/bank-account',
    meta: { requiresAuth: true },
    component: () => import('@/views/bankAccounts/BankAccountsView.vue'),
    children: [
      {
        // BankListView will be rendered inside BankAccount's <router-view>
        // when '/bank-account' is matched
        path: '/',
        name: 'Bank accounts',
        component: () => import('@/views/bankAccounts/BankListView.vue'),
      },
      {
        // CreateBankAccountView will be rendered inside BankAccount's <router-view>
        // when '/bank-account/create' is matched
        path: 'create',
        name: 'Create Bank Account',
        component: () =>
          import('@/views/bankAccounts/CreateBankAccountView.vue'),
      },
      {
        // CreateMandateView will be rendered inside BankAccount's <router-view>
        // when '/bank-account/:accountId/create-mandate' is matched
        path: ':accountId/create-mandate',
        name: 'Create Mandate',
        component: () => import('@/views/bankAccounts/CreateMandateView.vue'),
      },
      {
        // CreateMandateView will be rendered inside BankAccount's <router-view>
        // when '/bank-account/:accountId' is matched
        path: ':accountId',
        name: 'Bank Account detail',
        component: () => import('@/views/bankAccounts/BankAccountDetailsView.vue'),
      },
    ],
  },
  {
    // redirect unknown routes to wallet
    path: '*',
    redirect: '/wallet',
  },
];

const router = new VueRouter({
  mode: isDevEnvironment ? 'hash' : 'history',
  base: '/',
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { MandateId, createWalletRedirect } = getQueryParams(to.query);

  // remove mandateId from mangopay bank mandate confirmation redirect
  if(MandateId){
    delete to.query.MandateId;

    window.history.replaceState(
       {},
       document.title,
       `${window.location.pathname}${window.location.hash}`,
       );
       
      return next(to);  
      }

  // handle route redirects
  if (LocalStorage.has('REDIRECT_TO')) {
    const redirect = LocalStorage.get('REDIRECT_TO') as string;
    LocalStorage.remove('REDIRECT_TO');

    return next(redirect);
  }

  const validSession = await validateSession();
  // routes that require authentication
  if (to.matched.some(route => route.meta.requiresAuth)) {
    if (!validSession) {
      // user not signed in. store route to use after cockpit signin redirection
      const { fullPath } = to;
      LocalStorage.add('REDIRECT_TO', fullPath);

      return redirectToLogin();
    }

    // check that user is already loaded, if not, fetch user data
    if (!emoneyDisabled && !store.getters['profile/user']) {
      await store.dispatch('profile/fetchIdToken');

      try {
        await store.dispatch('profile/validateUser');
      } catch (error) {
        if (error.status !== 200) {

          // user does not have a wallet. send back to user service
          const { fullPath } = to;
          LocalStorage.add('REDIRECT_TO', fullPath);

          if(error.status === 429) {
            // rate limit in mangopay has been reached
            // prevent application to redirect user to user service to avoid infinite redirections loop
            return Promise.reject(null);
          }

          return redirectToOnboarding();
        }
      }
    }

    // fetch bank accounts
    if (!emoneyDisabled && !store.getters['mangoPay/bankAccounts']) {
      await store.dispatch('mangoPay/fetchUserBankAccounts');
    }
  }

  return next();
});

export default router;
